/**
 * O custo do OpenCode (F16; D14 e D20).
 *
 * **Aqui o dinheiro é real**, e é isso que separa este arquivo de `uso/precos.ts`.
 * No Claude o USD é um PESO — a conta é assinatura, e o custo equivalente só
 * serve para ratear a cota. No Codex nem isso: o peso é uma fórmula em tokens.
 * O OpenCode paga por token, com a chave do OpenRouter, e REPORTA o custo de
 * cada mensagem (`info.cost`, medido no OC-23: US$ 0,00191 num turno de duas
 * voltas no GLM-5.3-flash, com a leitura de cache separada).
 *
 * Por isso a regra daqui é a oposta de "estimar": o número que vale é o que o
 * motor relatou (`custoUsd` do `uso-do-motor` e do `turno_fim`). Sem ele, o
 * custo é `null` — "não se sabe" —, nunca uma conta nossa sobre a tabela de
 * preço, que viraria um número com cara de fatura sem ninguém ter medido.
 */

import type { PesoDoUso, UsoCodex } from '../uso/precos';

/**
 * O `usage` de uma chamada do OpenCode, na forma do evento `uso-do-motor`: a
 * convenção de tokens do Codex (a entrada INCLUI o cache) mais o custo que o
 * motor relatou. O daemon faz a conversão; este tipo é o que chega.
 */
export interface UsoOpencode extends UsoCodex {
  /** `info.cost` da mensagem, em US$. Ausente é "o motor não disse". */
  custoUsd?: number;
}

/**
 * O custo de UMA chamada: o relatado, ou `null`.
 *
 * Número negativo ou não finito não é custo, é defeito do lado de lá — e
 * devolver `null` é melhor que somar lixo no total da semana.
 */
export function custoDoUso(uso: { custoUsd?: number | null }): number | null {
  const c = uso.custoUsd;
  if (c === undefined || c === null) return null;
  if (!Number.isFinite(c) || c < 0) return null;
  return c;
}

/**
 * Soma custos, distinguindo "zero" de "não se sabe".
 *
 * Todos desconhecidos dá `null` (a tela escreve "sem custo relatado"); algum
 * conhecido dá a soma dos conhecidos, e `parcial: true` avisa que ficou chamada
 * de fora. Somar desconhecido como zero faria a semana parecer mais barata do
 * que foi, calada.
 */
export function somarCustos(custos: readonly (number | null)[]): { totalUsd: number | null; parcial: boolean } {
  const conhecidos = custos.filter((c): c is number => c !== null);
  if (conhecidos.length === 0) return { totalUsd: null, parcial: false };
  return {
    totalUsd: conhecidos.reduce((a, b) => a + b, 0),
    parcial: conhecidos.length < custos.length,
  };
}

/**
 * O peso de uma chamada do OpenCode no rateio: o próprio custo em US$.
 *
 * Mesma escala do peso do Claude (USD), mas NUNCA somados entre si — o rateio
 * é por motor, porque cada motor é uma conta, e a do OpenCode nem tem cota
 * para ratear: a tela mostra US$ sem % (D20). Sem custo relatado, peso zero e
 * `custoEquivUsd: null`, a mesma regra do modelo do Claude sem preço conhecido.
 */
export function pesoDoUsoOpencode(uso: UsoOpencode): PesoDoUso {
  const custo = custoDoUso(uso);
  return { peso: custo ?? 0, custoEquivUsd: custo };
}
